import axios from 'axios';
import { msalInstance, azureLoginRequest } from 'config/auth';
import { AUTH } from 'constants/authConstants';
import { EDIT_PROFILE, REST_METHOD, REST_URL } from './../constants';
import request, { requestUser, requestWithRetry } from './api';
import AES from 'crypto-js/aes';
import CryptoJS from 'crypto-js';
import { UserInfo } from 'models';
import {oktaAuth} from 'config/oktaAuth';
import AppService from './app.service';

interface SearchUserResponse {
  totalCount: number;
  items: UserInfo[];
}

export interface WalkupLocationItem {
  name: string;
  sys_id: string;
}

const UserService = {
  getUserDetailByUserName: (user_name: string, photo_size: string = '504x504') => {
    try {
      const requestParams = {
        url: `${REST_URL.CMDB}${REST_URL.USERINFO}`,
        method: `${REST_METHOD.GET}`,
        queryParams: { user_name: user_name, photo_size: photo_size },
      };
      return request(requestParams);
    } catch (error) {
      throw error;
    }
  },

  getUserDetailByEmail: (user_email: string, photo_size: string = '504x504') => {
    try {
      const requestParams = {
        url: `${REST_URL.CMDB}${REST_URL.USERINFO}`,
        method: `${REST_METHOD.GET}`,
        queryParams: { user_email: user_email, photo_size: photo_size },
      };
      return requestUser(requestParams);
    } catch (error) {
      throw error;
    }
  },
  getSettings: async () => {
    try {
      const requestParams = {
        url: `${REST_URL.GET_SETTINGS}`,
        method: `${REST_METHOD.GET}`,
      };
      return requestWithRetry(requestParams);
    } catch (error) {
      throw error
    }
  },

  saveCurrentUser: (data: any, encryptionKey: string) => {
    var encrypted = AES.encrypt(JSON.stringify(data), encryptionKey).toString();
    localStorage.setItem(AUTH.CURRENT_USER, encrypted);
    // localStorage.setItem(AUTH.CURRENT_USER, JSON.stringify(data));
  },

  getCurrentUser: (encryptionKey: string) => {
    let currentUser = localStorage.getItem(AUTH.CURRENT_USER);
    if (currentUser && encryptionKey) {
      var decrypted = AES.decrypt(currentUser, encryptionKey).toString(CryptoJS.enc.Utf8);
      return JSON.parse(decrypted);
    }
    // if (currentUser) {
    //   return JSON.parse(currentUser);
    // }
    return null;
  },
  checkUserCountry(userData: any) {
    let result = '';
    if (!!userData) {
      !!userData.country ? result = userData.country : (!!userData.cscCountry ? result = userData.cscCountry : result = userData.cscCountry2);
    }
    return result;
  },
  checkUserPilotMode: async (userName: string) => {
    try {
      const requestParams = {
        url: `${REST_URL.CMDB}${REST_URL.CHECK_USER_PILOT_MODE}`,
        method: `${REST_METHOD.GET}`,
        queryParams: { user_name: userName },
      };
      const response = await requestUser(requestParams);
      return response;
    } catch (error) {
      throw error;
    }
  },
  checkUserDxcGear: async (userEmail: string, userCountry: string, userType: string) => {
    try {
      const requestParams = {
        url: `${REST_URL.DXC_GEAR}`,
        method: `${REST_METHOD.GET}`,
        queryParams: { user_email: userEmail, user_country: userCountry, user_type: userType },
      };
      const response = await request(requestParams);
      return response;
    } catch (error) {
      throw error;
    }
  },

  clearAllByUser: async (userName: string) => {
    try {
      const requestParams = {
        url: `${REST_URL.CACHE}${REST_URL.CLEAR_ALL_BY_USER}`,
        method: `${REST_METHOD.POST}`,
        queryParams: { user_name: userName },
      };
      const response = await request(requestParams);
      return response;
    } catch (error) {
      throw error;
    }
  },

  getPreferences: async (userUID: string) => {
    try {
      const requestParams = {
        url: `${REST_URL.CMDB}${REST_URL.GET_REFERENCES}`,
        method: `${REST_METHOD.GET}`,
        queryParams: { user_uid: userUID },
      };
      const response = await requestUser(requestParams);
      return response;
    } catch (error) {
      throw error;
    }
  },

  upsertPreferences: async (data: any) => {
    try {
      const requestParams = {
        url: `${REST_URL.CMDB}${REST_URL.UPSERT_REFERENCES}`,
        method: `${REST_METHOD.PUT}`,
        data: data
      };
      const response = await requestUser(requestParams);
      return response;
    } catch (error) {
      throw error;
    }
  },

  saveDelegation: async (data:any, delegation_uid?: string) => {
    try{
      const requestParams = {
        url: `${REST_URL.CMDB}${REST_URL.SAVE_DELEGATION}`,
        queryParams: {
          ...(delegation_uid ? { delegation_uid: delegation_uid } : {})
        },
        method: `${REST_METHOD.PUT}`,
        data: data
      };
      const response = await requestUser(requestParams);
      return response;
    } catch (error) {
      throw error;
    }
  },

  getDelegation: async (user_uid: string, by?: string, valid_time?: string, approvals?: string) => {
    try {
      const requestParams = {
        url: `${REST_URL.CMDB}${REST_URL.GET_DELEGATION}`,
        method: `${REST_METHOD.GET}`,
        queryParams: {
          user_uid,
          ...(by ? { by: by } : {}),
          ...(valid_time ? {valid_time : valid_time} : {}),
          ...(approvals ? {approvals: approvals} : {})
        },
      };
      const response = await requestUser(requestParams);
      return response;
    } catch (error) {
      throw error;
    }
  },

  saveTimeFormat: (data: string) => {
    localStorage.setItem(EDIT_PROFILE.TIME_FORMAT_SETTING, data);
  },
  getPwChangeDate: async function(graphAzureBaseUrl: string) {
    if(AppService.isAuthenticatedByAzure()) {
      const [account] = msalInstance.getAllAccounts();
      return msalInstance
          .acquireTokenSilent({ ...azureLoginRequest, account })
          .then((res: any) => axios.get(
              `${graphAzureBaseUrl}/me?$select=lastPasswordChangeDateTime`,
              {
                method: 'GET',
                headers: {
                  Authorization: `Bearer ${res.accessToken}`,
                },
              },
            )).then((val: any) => val.data.lastPasswordChangeDateTime);
    } else {
      const rs = (await oktaAuth.session.get().then((val: any) => {
        if(val && val.user) {
          return val.user()
        }
      }))?.passwordChanged
      return rs
    }
  },
  searchUser: async (params: { manager_uid?: string, keyword?: string }): Promise<SearchUserResponse> => {
    try {
      const requestParams = {
        url: `${REST_URL.CMDB}${REST_URL.USER_SEARCH}`,
        method: `${REST_METHOD.GET}`,
        queryParams: params,
      };
      const response = await requestUser(requestParams);
      return response?.data;
    } catch (error) {
      throw error;
    }
  },
  getDelegationUser: async (params: { manager_uid?: string, keyword?: string }): Promise<SearchUserResponse> => {
    try {
      const requestParams = {
        url: `${REST_URL.CMDB}${REST_URL.GET_DELEGATION_USER}`,
        method: `${REST_METHOD.GET}`,
        queryParams: params,
      };
      const response = await requestUser(requestParams);
      return response?.data;
    } catch (error) {
      throw error;
    }
  },

  getWalkupLocation: async (params: { location: string; }): Promise<WalkupLocationItem[]> => {
    try{
      const requestParams = {
        url: `${REST_URL.CMDB}${REST_URL.WALK_UP_LOCATION}`,
        method: `${REST_METHOD.GET}`,
        queryParams: params,
      };

      const response = await requestUser(requestParams);
      return response?.data;
    
    } catch(error) {
      throw error;
    }
  },
  getUserOrg: async (user_uid: string) => {
    try {
      const requestParams = {
        url: `${REST_URL.CMDB}${REST_URL.USERS}/${user_uid}${REST_URL.ORG}`,
        method: `${REST_METHOD.GET}`,
      };
      const response = await requestUser(requestParams);
      return response.data;
    } catch (error) {
      throw error;
    }
  },
};
export default UserService;
